/* eslint-disable react/no-array-index-key */
import React, { useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useTheme } from '@emotion/react';
import { Element } from 'react-scroll';

import { Skeleton, Stack } from '@mui/material';
import { useSelector } from 'react-redux';
import useClasses from '../../../../../hooks/legacy/useClasses';

import styles from './RestaurantContactView.style';
import Map from '../../../../layout/Map';
import { getConfig } from '../../../../../AppConfig';
import { getImage } from '../../../../../util/Helpers';

function RestaurantContactView() {
  const classes = useClasses(styles);
  const theme = useTheme();
  const config = getConfig();

  const daysOfWeek = [
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
    'Sunday',
  ];

  const background = getImage(config, 'contact_hours', '');

  const contactLogo = useMemo(() => {
    let result = [];
    if (config?.images?.length) {
      result = config.images.filter(
        (img) => img.name === 'site_logo' && img.version === 1
      );
    }

    return result?.length && result[0]?.url ? result[0].url : '';
  }, [config.images]);

  const { restaurantDetails } = useSelector(
    ({ restaurant: selectedRestaurant }) => ({
      restaurantDetails: selectedRestaurant?.selectedRestaurantData,
    })
  );

  const [scheduleData, setScheduleData] = useState([
    {
      Monday: [],
      Tuesday: [],
      Wednesday: [],
      Thursday: [],
      Friday: [],
      Saturday: [],
      Sunday: [],
    },
  ]);

  function convertSchedulesToRows(schedules) {
    const updatedSchedule = {};
    daysOfWeek.forEach((dayName) => {
      updatedSchedule[dayName] = [];
    });

    for (let day = 0; day < 7; day++) {
      const dayName = daysOfWeek[day];
      if (schedules[day].openings.length > 0) {
        schedules[day].openings.forEach((opening) => {
          let openingHour = Math.floor(opening.open / 3600);
          const openingMinute = Math.floor((opening.open % 3600) / 60);
          let openingAmPm = 'AM';
          if (openingHour >= 12) {
            openingAmPm = 'PM';
            if (openingHour > 12) {
              openingHour -= 12;
            }
          } else if (openingHour === 0) {
            openingHour = 12; // midnight edge case
          }

          let closingHour = Math.floor(opening.close / 3600);
          const closingMinute = Math.floor((opening.close % 3600) / 60);
          let closingAmPm = 'AM';
          if (closingHour >= 12) {
            closingAmPm = 'PM';
            if (closingHour > 12) {
              closingHour -= 12;
            }
          } else if (closingHour === 0) {
            closingHour = 12; // midnight edge case
          }

          const shiftText = `${String(openingHour).padStart(2, '0')}:${String(
            openingMinute
          ).padStart(2, '0')}${openingAmPm} - ${String(closingHour).padStart(
            2,
            '0'
          )}:${String(closingMinute).padStart(2, '0')}${closingAmPm}`;

          updatedSchedule[dayName].push(shiftText);
        });
      }
    }

    setScheduleData([updatedSchedule]);
  }

  useEffect(() => {
    if (restaurantDetails && restaurantDetails?.schedules) {
      convertSchedulesToRows(
        restaurantDetails?.schedules?.business ??
          restaurantDetails?.schedules?.takeout ??
          restaurantDetails?.schedules?.delivery
      );
    }
  }, [restaurantDetails]);

  const mapData = useMemo(() => {
    if (restaurantDetails) {
      const result = [
        {
          ...restaurantDetails,
          location: {
            latitude: restaurantDetails?.latitude ?? 0,
            longitude: restaurantDetails?.longitude ?? 0,
          },
        },
      ];

      return result;
    }
    return [];
  }, [restaurantDetails]);

  return (
    <Box data-test-id="restaurantContactView" sx={{ pl: 0, pr: 0 }}>
      <Box
        component="img"
        sx={{
          width: '100%',
          maxHeight: '512px',
          objectFit: 'cover',
          objectPosition: 'center',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}
        src={background?.includes('https') ? background : `https:${background}`}
        className={classes.restaurantImg}
      />
      <Box
        sx={{
          mb: 0,
          ml: '10%',
          mr: '10%',
          mt: -37,
          px: 0,
          py: 0,
          position: 'relative',
          background: 'white',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          [theme.breakpoints.down('sm')]: {
            ml: 2,
            mr: 2,
            px: 0,
            py: 0,
            my: 1,
          },
          borderTopLeftRadius: 20,
          borderTopRightRadius: 20,
        }}
        height="auto"
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            textAlign: 'center',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 18,
            [theme.breakpoints.down('sm')]: {
              mb: 3,
            },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              textAlign: 'center',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 4,
              mt: 4,
            }}
          >
            <Typography variant="h5" color="black">
              Hours
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              textAlign: 'center',
              alignItems: 'center',
              justifyContent: 'space-evenly',
              pl: 6,
              pr: 6,
            }}
            width="100%"
            height="auto"
            data-test-id="hours-wrap"
          >
            {daysOfWeek.map((day) => (
              <Box
                key={day}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  m: 2,
                  minHeight: '120px',
                }}
              >
                <Typography variant="body1" color="black" fontWeight={400}>
                  {day}
                </Typography>
                {scheduleData.map((schedule, index) =>
                  schedule[day].length > 0 ? (
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                      }}
                    >
                      {schedule[day].map((scheduleItem) => (
                        <Typography
                          key={`time_${index}`}
                          variant="body2"
                          color="black"
                          fontWeight={500}
                        >
                          {scheduleItem}
                        </Typography>
                      ))}
                    </Box>
                  ) : (
                    <Typography
                      key={`time_${index}`}
                      variant="body2"
                      color="black"
                      fontWeight={500}
                    >
                      Closed
                    </Typography>
                  )
                )}
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
      <Element name="contact">
        <Box
          sx={{
            height: '500px',
            mb: 5,
            display: 'flex',
            alignItems: 'center',
            [theme.breakpoints.down('sm')]: {
              flexDirection: 'column',
              height: 'auto',
            },
          }}
        >
          <Box
            sx={{
              width: '50%',
              height: '100%',
              [theme.breakpoints.down('sm')]: {
                width: '100%',
                height: '500px',
              },
            }}
          >
            <Map
              list={mapData}
              isDesktop
              isFullScreen={false}
              restaurantLandingPage
            />
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              width: '50%',
              p: 3,
              [theme.breakpoints.down('sm')]: {
                width: '100%',
                height: '500px',
              },
            }}
          >
            <Stack
              direction="row"
              flexDirection="column"
              justifyContent="center"
              alignItems="center"
              mb={1}
            >
              <Box className={classes.restaurantBrand}>
                {contactLogo ? (
                  <Box
                    maxHeight="256px"
                    width="auto"
                    component="img"
                    src={
                      contactLogo?.includes('https')
                        ? contactLogo
                        : `https:${contactLogo}`
                    }
                    sx={{ borderRadius: 2 }}
                  />
                ) : (
                  <Skeleton variant="rectangular" width="100%" height="100%" />
                )}
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                }}
              >
                <Typography
                  variant="h6"
                  color="black"
                  fontWeight={400}
                  textAlign="center"
                  my={2}
                >
                  {restaurantDetails?.name ?? 'Restaurant Name'}
                </Typography>
                <Typography variant="body1" color="black" fontWeight={400}>
                  {`Address: ${
                    restaurantDetails?.address ?? 'street address'
                  }, ${restaurantDetails?.city ?? 'City'}, ${
                    restaurantDetails?.state ?? 'State'
                  }, ${restaurantDetails?.zipcode ?? 'Zipcode'}`}
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    my: 2,
                  }}
                >
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="body1" color="black" fontWeight={600}>
                      Email:
                    </Typography>
                    <Typography variant="body1" color="black" fontWeight={400}>
                      {restaurantDetails?.email ?? 'contact email'}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="body1" color="black" fontWeight={600}>
                      Phone:
                    </Typography>
                    <Typography variant="body1" color="black" fontWeight={400}>
                      {restaurantDetails?.phone ?? 'contact phone'}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Stack>
          </Box>
        </Box>
      </Element>
    </Box>
  );
}

export default RestaurantContactView;
